import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Download, BarChart2, Users, FileText, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [downloadError, setDownloadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    Promise.all([api.reports.sessions(), api.teams.list()])
      .then(([s, t]) => { setSessions(s || []); setTeams(t || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = teamFilter ? sessions.filter(s => s.team_name === teamFilter) : sessions;

  if (loading) return (
    <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
      <p className="text-muted pulse">Loading reports...</p>
    </div>
  );

  const handleDownload = async (fn) => {
    setDownloadError('');
    try { await fn(); }
    catch (e) { setDownloadError(e.message || 'Download failed. Please try again.'); }
  };

  return (
    <div className="page">
      {downloadError && (
        <div className="alert alert-error mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{downloadError}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setDownloadError('')}>✕</button>
        </div>
      )}
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>Reports</h1>
          <p className="text-muted">History across all retro sessions.</p>
        </div>
        <select className="input" value={teamFilter} onChange={e => setTeamFilter(e.target.value)}
          style={{ width: 200 }}>
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
      </div>

      {error && <div className="toast toast-error mb-4">{error}</div>}

      {/* Summary stats */}
      {sessions.length > 0 && (
        <div className="grid-3 mb-6" style={{ gap: 16 }}>
          <StatCard icon={<BarChart2 size={18} />} label="Total Sessions" value={sessions.length} color="var(--accent)" />
          <StatCard icon={<Users size={18} />} label="Avg Participants" value={Math.round(sessions.reduce((s, x) => s + (x.participant_count || 0), 0) / sessions.length)} color="var(--green)" />
          <StatCard icon={<FileText size={18} />} label="Total Actions" value={sessions.reduce((s, x) => s + (x.action_count || 0) + (x.change_count || 0), 0)} color="var(--yellow)" />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>📊</div>
          <h2 style={{ marginBottom: 8 }}>No sessions yet</h2>
          <p className="text-muted">Complete retro sessions will appear here.</p>
        </div>
      ) : (
        <div className="flex-col gap-3">
          {filtered.map(s => (
            <div key={s.session_id} className="card" style={{ padding: '20px 24px' }}>
              <div className="flex items-start gap-4" style={{ flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h3 style={{ fontSize: '1rem' }}>{s.session_name}</h3>
                    <span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-muted'}`}>{s.status}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="badge badge-purple">{s.team_name}</span>
                    <span className="text-xs text-muted flex items-center gap-1"><Calendar size={10} /> {new Date(s.created_at).toLocaleDateString()}</span>
                    <span className="text-xs text-muted flex items-center gap-1"><Users size={10} /> {s.participant_count} participants</span>
                  </div>
                </div>

                {/* Card counts */}
                <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                  {[
                    { label: 'Start', count: s.start_count, color: 'var(--phase-start)' },
                    { label: 'Stop', count: s.stop_count, color: 'var(--phase-stop)' },
                    { label: 'Continue', count: s.continue_count, color: 'var(--phase-continue)' },
                    { label: 'Kudos', count: s.kudos_count, color: 'var(--phase-kudos)' },
                    { label: 'Actions', count: s.action_count, color: 'var(--green)' },
                    { label: 'Changes', count: s.change_count, color: 'var(--yellow)' },
                  ].map(({ label, count, color }) => count > 0 ? (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 700, color }}>{count}</div>
                      <div className="text-xs text-muted">{label}</div>
                    </div>
                  ) : null)}
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  {s.status === 'active' && (
                    <button className="btn btn-primary btn-sm" onClick={() => navigate(`/session/${s.session_id}`)}>
                      → Open
                    </button>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleDownload(() => api.downloadPdf(s.session_id))}
                  >
                    <Download size={12} /> PDF
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleDownload(() => api.downloadCsv(s.session_id))}
                  >
                    <Download size={12} /> CSV
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleDownload(() => api.downloadConfluence(s.session_id))}
                    title="Download as HTML for Confluence import"
                  >
                    <Download size={12} /> Confluence
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="card flex items-center gap-4" style={{ padding: '16px 20px' }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `color-mix(in srgb, ${color} 15%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '1.6rem', fontWeight: 800 }}>{value}</div>
        <div className="text-xs text-muted">{label}</div>
      </div>
    </div>
  );
}
